/*
  Language detection logic using a Web Worker.
  - Ensures a singleton module worker at `src/workers/lang-detector.worker.js`
  - Provides a simple Promise-based API: detectLanguage(text, options?)

  Usage:
    import { detectLanguage, terminateLangDetector } from '@/modules/global/langDetection'
    const languages = await detectLanguage('Hello world')

  Options:
    - options.allowedLangs?: string[]  // e.g. ['en', 'de'] to filter the result set
*/

let workerInstance = null
let sequenceId = 0
const pendingRequests = new Map()

function ensureWorker() {
  if (workerInstance) {
    return workerInstance
  }

  workerInstance = new Worker(/* webpackIgnore: true */ '/workers/lang-detector.worker.js')

  workerInstance.onmessage = (e) => {
    const {
      id,
      ok,
      result,
      error,
    } = e.data || {}

    if (!id) {
      return
    }

    const resolver = pendingRequests.get(id)
    if (!resolver) {
      return
    }

    pendingRequests.delete(id)

    if (ok) {
      resolver.resolve(result)
    } else {
      resolver.reject(new Error(error || 'Language detection failed'))
    }
  }

  workerInstance.onerror = (e) => {
    // Fail all pending requests on worker error
    const err = new Error(e && e.message ? e.message : 'Worker error')
    for (const [, resolver] of pendingRequests) {
      resolver.reject(err)
    }
    pendingRequests.clear()
  }

  return workerInstance
}

function callWorker(payload) {
  const worker = ensureWorker()

  sequenceId += 1
  const id = sequenceId

  return new Promise((resolve, reject) => {
    pendingRequests.set(id, { resolve, reject })
    worker.postMessage({ id, ...payload })
  })
}

export async function detectLanguage(text, options = undefined) {
  if (!text || typeof text !== 'string') {
    return []
  }

  const result = await callWorker({ text, options })
  return Array.isArray(result) ? result : []
}

export function terminateLangDetector() {
  if (!workerInstance) {
    return
  }

  try {
    workerInstance.terminate()
  } catch (_) {
    // ignore
  }

  workerInstance = null
  pendingRequests.clear()
}
